/**
 * Right-hand intelligence panel for the selected node or edge: entity
 * metadata, aliases (why records joined), datasets, PLA classification
 * evidence, connection summary, relationship list, expand action, the full
 * procurement-record viewer, and the evidence list tying every relationship
 * back to its underlying API record.
 */
import { FC, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  IntelEdge,
  IntelNode,
  IntelNodeType,
  IntelligenceGraphSnapshot,
} from '../../types/intelligenceGraph';
import { NODE_TYPE_META } from './nodeStyles';
import { EvidencePanel } from './EvidencePanel';

const EDGE_LABELS: Record<string, string> = {
  AFFILIATED_WITH: 'Affiliated with',
  AUTHORED: 'Authored',
  COAUTHORED_WITH: 'Co-authored with',
  RESEARCHES: 'Researches',
  COLLABORATED_WITH: 'Collaborated with',
  RELATED_TECHNOLOGY: 'Related technology',
  PROCURED: 'Procured',
  SUPPLIED: 'Supplied',
  BID_ON: 'Bid on',
  BROKERED_BY: 'Brokered by',
  CONTACT_FOR: 'Contact for',
  USES_EMAIL: 'Uses email',
  USES_DOMAIN: 'Uses domain',
  MENTIONED_IN: 'Mentioned in',
};

export function edgeTypeLabel(type: string): string {
  return EDGE_LABELS[type] ?? type;
}

/** Meta keys rendered by dedicated sections rather than the generic list. */
const HIDDEN_META_KEYS = ['content', 'publicationId', 'recordId'];

/**
 * Internal Data Abyss publication route for a research node — the primary
 * Open Publication action always navigates inside Data Abyss, never to
 * external publisher/CNKI pages.
 */
function publicationHref(node: IntelNode): string | undefined {
  const publicationId =
    node.meta?.publicationId ??
    node.evidence.find((e) => e.sourceDataset === 'publications')
      ?.sourceRecordId;
  return publicationId
    ? `/publication/${encodeURIComponent(publicationId)}`
    : undefined;
}

interface EntityDetailsPanelProps {
  node?: IntelNode | null;
  edge?: IntelEdge | null;
  snapshot: IntelligenceGraphSnapshot;
  expandable?: boolean;
  expanding?: boolean;
  onExpand?: (node: IntelNode) => void;
  onSelectNode?: (nodeId: string) => void;
  /** Highlights a set of node ids in the graph (connection category focus). */
  onHighlightNodes?: (nodeIds: string[]) => void;
  onClose?: () => void;
}

function NodeHeader({ node }: { node: IntelNode }) {
  const meta = NODE_TYPE_META[node.type];
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          backgroundColor: `${meta.color}22`,
          borderRadius: 1.5,
          p: 1,
          display: 'flex',
          flexShrink: 0,
        }}
      >
        <Icon icon={meta.icon} color={meta.color} width={22} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}
        >
          {node.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {meta.label}
          {node.isRoot ? ' · Search root' : ''}
        </Typography>
      </Box>
    </Stack>
  );
}

/** Ordered, labelled field layout for the procurement details section. */
const PROCUREMENT_FIELDS: { key: string; label: string }[] = [
  { key: 'buyer', label: 'Buyer' },
  { key: 'supplier', label: 'Supplier' },
  { key: 'agency', label: 'Procurement Agency' },
  { key: 'amount', label: 'Amount' },
  { key: 'tenderCode', label: 'Tender Code' },
  { key: 'createTime', label: 'Date' },
  { key: 'area', label: 'Location' },
  { key: 'category', label: 'Category' },
  { key: 'buyerContact', label: 'Buyer Contact' },
  { key: 'buyerContactPhone', label: 'Buyer Contact Phone' },
  { key: 'agencyContact', label: 'Agency Contact' },
  { key: 'agencyContactPhone', label: 'Agency Contact Phone' },
  { key: 'emails', label: 'Emails' },
];

/**
 * Structured procurement evidence + full-record viewer. The full `content`
 * is already returned by the PLA Contracts Agent Search response, so the
 * complete procurement text renders without a second API request.
 */
function ProcurementDetails({ node }: { node: IntelNode }) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const meta = node.meta ?? {};
  const fields = PROCUREMENT_FIELDS.filter(({ key }) => meta[key]);
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Procurement Record
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {fields.map(({ key, label }) => (
          <Box key={key}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {meta[key]}
            </Typography>
          </Box>
        ))}
      </Stack>
      {meta.content && (
        <>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            sx={{ mt: 1.5 }}
            startIcon={<Icon icon="mdi:file-document-outline" />}
            onClick={() => setViewerOpen(true)}
          >
            View Full Procurement
          </Button>
          <Dialog
            open={viewerOpen}
            onClose={() => setViewerOpen(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ pr: 6 }}>
              <Typography
                component="div"
                variant="h6"
                sx={{ wordBreak: 'break-word' }}
              >
                {node.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Full Procurement Record
              </Typography>
              <IconButton
                size="small"
                onClick={() => setViewerOpen(false)}
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <Icon icon="mdi:close" width={18} />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {fields.map(({ key, label }) => (
                  <Typography key={key} variant="body2">
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                    >
                      {label}:{' '}
                    </Typography>
                    {meta[key]}
                  </Typography>
                ))}
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="overline" color="text.secondary">
                Full Text
              </Typography>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.7,
                }}
              >
                {meta.content}
              </Typography>
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
}

/** Connection counts grouped by neighbor type; clicking focuses that type. */
function ConnectionsSection({
  node,
  snapshot,
  onHighlightNodes,
}: {
  node: IntelNode;
  snapshot: IntelligenceGraphSnapshot;
  onHighlightNodes?: (nodeIds: string[]) => void;
}) {
  const groups = useMemo(() => {
    const nodeById = new Map(snapshot.nodes.map((n) => [n.id, n]));
    const byType = new Map<IntelNodeType, string[]>();
    snapshot.edges.forEach((edge) => {
      if (edge.source !== node.id && edge.target !== node.id) return;
      const otherId = edge.source === node.id ? edge.target : edge.source;
      const other = nodeById.get(otherId);
      if (!other) return;
      const list = byType.get(other.type) ?? [];
      if (!list.includes(otherId)) list.push(otherId);
      byType.set(other.type, list);
    });
    return Array.from(byType.entries()).sort(
      (a, b) => b[1].length - a[1].length,
    );
  }, [node, snapshot]);

  if (groups.length === 0) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="overline" color="text.secondary">
        Connections
      </Typography>
      <Stack spacing={0.25}>
        {groups.map(([type, ids]) => {
          const meta = NODE_TYPE_META[type];
          return (
            <Button
              key={type}
              size="small"
              variant="text"
              sx={{
                justifyContent: 'space-between',
                textTransform: 'none',
                minHeight: 0,
                py: 0.25,
              }}
              startIcon={
                <Icon icon={meta.icon} color={meta.color} width={14} />
              }
              endIcon={
                <Typography variant="caption" fontWeight={700}>
                  {ids.length}
                </Typography>
              }
              onClick={() => onHighlightNodes?.([node.id, ...ids])}
            >
              <Typography variant="caption" sx={{ flex: 1, textAlign: 'left' }}>
                {meta.label}
              </Typography>
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}

export const EntityDetailsPanel: FC<EntityDetailsPanelProps> = ({
  node,
  edge,
  snapshot,
  expandable,
  expanding,
  onExpand,
  onSelectNode,
  onHighlightNodes,
  onClose,
}) => {
  if (!node && !edge) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Entity Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select a node or relationship in the graph to inspect its metadata,
          relationships and underlying evidence.
        </Typography>
      </Box>
    );
  }

  // ---- Edge selected: show relationship + provenance ----
  if (edge) {
    const sourceNode = snapshot.nodes.find((n) => n.id === edge.source);
    const targetNode = snapshot.nodes.find((n) => n.id === edge.target);
    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="overline" color="text.secondary">
            Relationship
          </Typography>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <Icon icon="mdi:close" width={16} />
            </IconButton>
          )}
        </Stack>
        <Typography variant="subtitle1" fontWeight={700}>
          {edgeTypeLabel(edge.type)}
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {[sourceNode, targetNode].map(
            (endpoint) =>
              endpoint && (
                <Button
                  key={endpoint.id}
                  size="small"
                  variant="text"
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  startIcon={
                    <Icon
                      icon={NODE_TYPE_META[endpoint.type].icon}
                      color={NODE_TYPE_META[endpoint.type].color}
                    />
                  }
                  onClick={() => onSelectNode?.(endpoint.id)}
                >
                  {endpoint.label}
                </Button>
              ),
          )}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
          <Chip
            size="small"
            color={edge.relationshipType === 'observed' ? 'success' : 'default'}
            variant="outlined"
            label={
              edge.relationshipType === 'observed'
                ? 'Observed relationship'
                : `Derived · confidence ${Math.round(edge.confidence * 100)}%`
            }
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <EvidencePanel evidence={edge.provenance} />
      </Box>
    );
  }

  // ---- Node selected ----
  if (!node) return null;
  const relationships = snapshot.edges.filter(
    (e) => e.source === node.id || e.target === node.id,
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <NodeHeader node={node} />
        {onClose && (
          <IconButton size="small" onClick={onClose}>
            <Icon icon="mdi:close" width={16} />
          </IconButton>
        )}
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.5 }}
      >
        {node.datasets.map((dataset) => (
          <Chip
            key={dataset}
            size="small"
            variant="outlined"
            label={
              dataset === 'publications' ? 'Publications' : 'PLA Contracts'
            }
          />
        ))}
        {node.datasets.length > 1 && (
          <Chip size="small" color="warning" label="Fused (both datasets)" />
        )}
        <Chip size="small" label={`${node.recordCount} records`} />
        <Chip size="small" label={`${relationships.length} relationships`} />
      </Stack>

      {node.plaEvidence && node.plaEvidence.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="overline" color="error">
            PLA / Military Classification
          </Typography>
          {node.plaEvidence.map((reason) => (
            <Typography key={reason} variant="caption" component="div">
              • {reason}
            </Typography>
          ))}
        </Box>
      )}

      {node.aliases.length > 1 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="overline" color="text.secondary">
            Source labels / aliases
          </Typography>
          {node.aliases.slice(0, 8).map((alias) => (
            <Typography
              key={alias}
              variant="caption"
              component="div"
              sx={{ wordBreak: 'break-word' }}
            >
              • {alias}
            </Typography>
          ))}
        </Box>
      )}

      {node.type === 'procurement' ? (
        <ProcurementDetails node={node} />
      ) : (
        node.meta && (
          <Stack spacing={0.25} sx={{ mt: 1.5 }}>
            {Object.entries(node.meta)
              .filter(
                ([key, value]) =>
                  Boolean(value) && !HIDDEN_META_KEYS.includes(key),
              )
              .slice(0, 8)
              .map(([key, value]) => (
                <Typography
                  key={key}
                  variant="caption"
                  color="text.secondary"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {key}: {value}
                </Typography>
              ))}
          </Stack>
        )
      )}

      {node.type === 'research' && publicationHref(node) && (
        <Button
          fullWidth
          variant="outlined"
          size="small"
          sx={{ mt: 1.5 }}
          startIcon={<Icon icon="mdi:book-open-variant" />}
          endIcon={<Icon icon="mdi:open-in-new" width={14} />}
          href={publicationHref(node) as string}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Publication
        </Button>
      )}

      {expandable && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          disabled={expanding}
          sx={{ mt: 2 }}
          startIcon={
            <Icon
              icon={expanding ? 'mdi:loading' : 'mdi:arrow-expand-all'}
              className={expanding ? 'spin' : undefined}
            />
          }
          onClick={() => onExpand?.(node)}
        >
          {expanding ? 'Expanding…' : 'Expand Node'}
        </Button>
      )}

      <Divider sx={{ my: 2 }} />

      <ConnectionsSection
        node={node}
        snapshot={snapshot}
        onHighlightNodes={onHighlightNodes}
      />

      <Typography variant="overline" color="text.secondary">
        Relationships ({relationships.length})
      </Typography>
      <Stack spacing={0.5} sx={{ maxHeight: 220, overflowY: 'auto' }}>
        {relationships.slice(0, 50).map((rel) => {
          const otherId = rel.source === node.id ? rel.target : rel.source;
          const other = snapshot.nodes.find((n) => n.id === otherId);
          if (!other) return null;
          return (
            <Tooltip
              key={`${rel.source}|${rel.type}|${rel.target}`}
              title={`${edgeTypeLabel(rel.type)} · ${rel.relationshipType} · ${
                rel.provenance.length
              } evidence record(s)`}
            >
              <Button
                fullWidth
                size="small"
                variant="text"
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  minHeight: 0,
                  py: 0.5,
                  '& .MuiButton-startIcon': { flexShrink: 0 },
                }}
                startIcon={
                  <Icon
                    icon={NODE_TYPE_META[other.type].icon}
                    color={NODE_TYPE_META[other.type].color}
                    width={14}
                  />
                }
                onClick={() => onSelectNode?.(other.id)}
              >
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ minWidth: 0, textAlign: 'left' }}
                >
                  {edgeTypeLabel(rel.type)} — {other.label}
                </Typography>
              </Button>
            </Tooltip>
          );
        })}
      </Stack>

      <Divider sx={{ my: 2 }} />
      <EvidencePanel evidence={node.evidence} />
    </Box>
  );
};
